import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";

export function SignaturePad({ value, onChange, readonly, testid }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(!!value);

  const ctxColor = () =>
    getComputedStyle(document.documentElement).classList?.contains?.("dark") ? "#f6d9e3" : "#2b1b3d";

  const setup = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = document.documentElement.classList.contains("dark") ? "#f6d9e3" : "#2b1b3d";
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
    }
  };

  useEffect(() => { setup(); /* eslint-disable-next-line */ }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => {
    if (readonly) return;
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current || readonly) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange("");
  };

  if (readonly) {
    return value ? (
      <img src={value} alt="signature" className="h-16 border rounded-lg bg-white p-1" data-testid={testid} />
    ) : (
      <div className="h-16 border rounded-lg flex items-center text-xs text-muted-foreground px-3">Not signed</div>
    );
  }

  return (
    <div className="space-y-1" data-testid={testid}>
      <canvas
        ref={canvasRef}
        className="w-full h-24 rounded-lg border-2 border-dashed bg-background touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">{hasInk ? "Signed" : "Sign above"}</span>
        <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={clear} data-testid={`${testid}-clear`}>
          <Eraser size={12} /> Clear
        </Button>
      </div>
    </div>
  );
}
